import { useState, useMemo, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { Routes, Route, Navigate } from 'react-router-dom';
import { lightTheme, darkTheme } from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BusinessProvider } from './context/BusinessContext';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import BusinessDetailPage from './pages/BusinessDetailPage';
import BookingPage from './pages/BookingPage';
import MyBookingsPage from './pages/MyBookingsPage';
import BusinessSettingsPage from './pages/BusinessSettingsPage';
import CalendarConfigPage from './pages/CalendarConfigPage';
import CalendarOverviewPage from './pages/CalendarOverviewPage';
import CalendarViewPage from './pages/CalendarViewPage';
import VoiceAgentPage from './pages/VoiceAgentPage';
import DemoCallPage from './pages/DemoCallPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ROUTES } from './constants/routes';


/**
 * Protected route wrapper
 */
const ProtectedRoute = ({ children, themeMode, toggleTheme }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return (
    <MainLayout themeMode={themeMode} toggleTheme={toggleTheme}>
      {children}
    </MainLayout>
  );
};

/**
 * Public route wrapper (redirects to home if authenticated)
 */
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (user) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return children;
};

/**
 * Main App component with routing
 */
const AppRoutes = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode ? savedMode === 'dark' : false;
  });

  useEffect(() => {
    localStorage.setItem('themeMode', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const theme = useMemo(() => (isDarkMode ? darkTheme : lightTheme), [isDarkMode]);
  const themeMode = isDarkMode ? 'dark' : 'light';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {/* Public Routes */}
        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicRoute>
              <Login themeMode={themeMode} toggleTheme={toggleTheme} />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.SIGNUP}
          element={
            <PublicRoute>
              <SignUp themeMode={themeMode} toggleTheme={toggleTheme} />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.BUSINESS_DETAIL}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <BusinessDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.BOOKING}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <BookingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.MY_BOOKINGS}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <MyBookingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.BUSINESS_SETTINGS}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <BusinessSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CALENDAR_CONFIG}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <CalendarConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CALENDAR_OVERVIEW}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <CalendarOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CALENDAR_VIEW}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <CalendarViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.VOICE_AGENT}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <VoiceAgentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DEMO_CALL}
          element={
            <ProtectedRoute themeMode={themeMode} toggleTheme={toggleTheme}>
              <DemoCallPage />
            </ProtectedRoute>
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
      </Routes>
    </ThemeProvider>
  );
};

/**
 * App wrapper with providers
 */
const App = () => {
  return (
    <AuthProvider>
      <BusinessProvider>
        <AppRoutes />
      </BusinessProvider>
    </AuthProvider>
  );
};

export default App;
